import { color as fplColor } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { FlexCenVer } from '@/UI/styles';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import type { AriaAttributes, KeyboardEvent, MouseEvent, ReactNode } from 'react';

interface ToolShareRowProps {
  /** 24×24 leading glyph slot. */
  icon?: ReactNode;
  label: ReactNode;
  /** Right-aligned slot — a role label, a chevron, or a button. */
  trail?: ReactNode;
  onClick?: (event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>) => void;
  disabled?: boolean;
  /** Fills on hover without an `onClick`, for rows whose actions live in the trail. */
  hoverable?: boolean;
  'aria-haspopup'?: AriaAttributes['aria-haspopup'];
  'aria-expanded'?: boolean;
}

/** One row of the Share tool modal's list: an 8px gutter around a 32px hover target. */
export function ToolShareRow({
  icon,
  label,
  trail,
  onClick,
  disabled = false,
  hoverable = false,
  'aria-haspopup': ariaHasPopup,
  'aria-expanded': ariaExpanded,
}: ToolShareRowProps) {
  const isFpl = useFplMigrationFlag();
  const isInteractive = Boolean(onClick) && !disabled;
  const hoverBg = isFpl ? fplColor.bgHover : color.Black84;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onClick?.(event);
  };

  return (
    <Box sx={{ alignSelf: 'stretch', px: 1, py: 0.25 }}>
      <FlexCenVer
        role={onClick ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        aria-disabled={onClick ? disabled : undefined}
        aria-haspopup={ariaHasPopup}
        aria-expanded={ariaExpanded}
        onClick={isInteractive ? onClick : undefined}
        onKeyDown={isInteractive ? handleKeyDown : undefined}
        sx={{
          justifyContent: 'space-between',
          gap: 1,
          p: 0.5,
          minHeight: 32,
          borderRadius: '5px',
          cursor: isInteractive ? 'pointer' : 'default',
          ...((isInteractive || hoverable) && { '&:hover': { backgroundColor: hoverBg } }),
        }}
      >
        <FlexCenVer sx={{ flex: 1, gap: 1, minWidth: 0 }}>
          {icon && (
            <FlexCenVer sx={{ width: 24, height: 24, flexShrink: 0, justifyContent: 'center' }}>{icon}</FlexCenVer>
          )}
          {label}
        </FlexCenVer>
        {trail}
      </FlexCenVer>
    </Box>
  );
}

export default ToolShareRow;
